import { useQuery } from "@tanstack/react-query"
import { z } from "zod"
import { auth } from "@/lib/firebase"

// Define a schema for the time series data
const timePointSchema = z.object({
  date: z.string(),
  count: z.number(),
})

const casesTimeSeriesSchema = z.object({
  data_points: z.array(timePointSchema),
  interval: z.enum(["day", "week", "month"]),
})

export type TimePoint = z.infer<typeof timePointSchema>
export type CasesTimeSeries = z.infer<typeof casesTimeSeriesSchema>

type IntervalType = "day" | "week" | "month"

const fetchCasesTimeSeries = async (interval: IntervalType, limit: number): Promise<CasesTimeSeries> => {
  const user = auth.currentUser
  if (!user) {
    throw new Error("User not found.")
  }
  
  const token = await user.getIdToken()
  
  const response = await fetch(
    `${import.meta.env.VITE_API_URL}/api/stats/cases-time-series/?interval=${interval}&limit=${limit}`,
    { 
      headers: { 
        Authorization: `Bearer ${token}` 
      }
    }
  )
  
  if (!response.ok) {
    throw new Error(`Failed to fetch cases time series: ${response.status} ${response.statusText}`)
  }
  
  const data = await response.json()
  return casesTimeSeriesSchema.parse(data)
}

export const useCasesTimeSeries = (interval: IntervalType = "day", limit: number = 30) => {
  return useQuery({
    queryKey: ["casesTimeSeries", interval, limit],
    queryFn: () => fetchCasesTimeSeries(interval, limit),
  })
} 